/** Additional checked orders, expressed as places in the original sentence. */
export type ArrangementOrders = readonly (readonly number[])[];

/**
 * Read checked orders, each using every tile exactly once.
 * @param value - The published orders, absent on older items.
 * @param count - The number of tiles.
 * @returns The orders, or nothing for malformed data.
 */
export function arrangementOrders(
  value: unknown,
  count: number,
): ArrangementOrders | undefined {
  if (value === undefined) {
    return [];
  }

  return Array.isArray(value) &&
    value.every(
      (order: unknown) =>
        Array.isArray(order) &&
        order.length === count &&
        new Set(order).size === count &&
        order.every(
          (place: unknown) =>
            typeof place === "number" &&
            Number.isInteger(place) &&
            place >= 0 &&
            place < count,
        ),
    )
    ? (value as ArrangementOrders)
    : undefined;
}

/**
 * The orders a sentence's tiles have been judged in, besides its own.
 *
 * Both are places in the sentence's pieces, the way `ArrangementOrders` are.
 * An accepted order says what the sentence says, and earns what the
 * sentence's own order earns. A refused order uses every piece and says
 * something else, or nothing, and earns what a wrong piece earns.
 * `src/content/liju/liju-word-orders.ts` is where they are recorded.
 */
export interface WordOrders {
  readonly accepted: ArrangementOrders;
  readonly refused: ArrangementOrders;
}

/** A sentence judged in no order but its own. */
export const noWordOrders: WordOrders = { accepted: [], refused: [] };

/**
 * Read a sentence's judged orders, each using every tile exactly once.
 * @param value - The published orders, absent on a sentence nobody judged.
 * @param count - The number of tiles.
 * @returns The orders, or nothing for malformed data.
 */
export function wordOrdersOf(
  value: unknown,
  count: number,
): WordOrders | undefined {
  if (value === undefined) {
    return noWordOrders;
  }

  if (typeof value !== "object" || value === null) {
    return undefined;
  }

  const held = value as Record<string, unknown>;
  const accepted = arrangementOrders(held["accepted"], count);
  const refused = arrangementOrders(held["refused"], count);

  return accepted === undefined || refused === undefined
    ? undefined
    : { accepted, refused };
}
